import { createContext, type ReactNode, useContext, useMemo } from "react";

/**
 * Where the app bar's back arrow goes.
 *
 * Two different backs share one button, and they belong at different levels:
 *
 *  - *Within* a module, back steps to the previous stage. Only the module screen
 *    knows its own stage order, so it passes `onBack` to `AppBar` directly.
 *  - *Out of* a module, back returns to the guide. That is flow-level routing,
 *    so `AppBar` reads it from here instead of every screen threading a prop
 *    down from `FlowShell` purely to hand it to its first stage.
 *
 * `AppBar` prefers a screen's own `onBack` and falls back to `exitToGuide`, so a
 * module's first stage gets the right behaviour by passing nothing at all. With
 * no provider above it (the loading screen, or a screen rendered in isolation)
 * there is nowhere to go, and the bar renders no back button rather than a dead
 * one.
 */
interface FlowNavApi {
  /** Leave the current module and return to the guide/home screen. */
  exitToGuide: () => void;
}

const FlowNavContext = createContext<FlowNavApi | null>(null);

/** Flow-level navigation, or null outside the flow. */
export function useFlowNav(): FlowNavApi | null {
  return useContext(FlowNavContext);
}

export function FlowNavProvider({ exitToGuide, children }: FlowNavApi & { children: ReactNode }) {
  const value = useMemo(() => ({ exitToGuide }), [exitToGuide]);
  return <FlowNavContext.Provider value={value}>{children}</FlowNavContext.Provider>;
}
